import { FpsRecordCallback, PlayerDataIntervals } from '../../types/webPlayer';

/**
 * FpsCtrl is a utility class for controlling a callback function to run at a specific frame per second rate.
 * It provides methods to start, pause, and step through frames at the designated FPS rate.
 */
export class FpsCtrl {
  isPlaying = false;

  time: number | null = null;

  frame = -1;

  tref: number | undefined = undefined;

  callback: undefined | Function = undefined;

  delay = 1000;

  fps = 25;

  multiplier = 1;

  fastPlay = false;

  constructor(fps: number, callback: Function, multiplier: number = 1, fastPlay = false) {
    this.delay = 1000 / fps;
    this.fps = fps;
    this.callback = callback;
    this.multiplier = multiplier;
    this.fastPlay = fastPlay;
  }

  loop = (timestamp: number) => {
    if (this.time === null) {
      this.time = timestamp;
    }

    const seg = Math.floor((timestamp - this.time) / (this.fastPlay ? 2 : this.delay));

    if (seg > this.frame) {
      this.frame = seg;

      if (this.callback) {
        this.callback({
          frame: this.frame * this.multiplier,
        });
      }
    }

    if (this.isPlaying) {
      this.tref = requestAnimationFrame(this.loop);
    }
  };

  setMultiplier = (value: number) => {
    this.multiplier = value;
  };

  setFastPlay = (value: boolean) => {
    this.fastPlay = value;
  };

  frameRate = (newfps: number) => {
    if (!newfps) {
      return this.fps;
    }

    this.fps = newfps;
    this.delay = 1000 / this.fps;
    this.frame = -1;
    this.time = null;

    return this.fps;
  };

  start = () => {
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.tref = requestAnimationFrame(this.loop);
    }
  };

  stepForward = (seconds: number) => {
    if (this.isPlaying) {
      this.pause();
    }
    this.frame += this.fps;
    this.time = (this.time || 0) + 1000 * seconds;

    if (this.callback) {
      this.callback({
        time: this.time,
        frame: this.frame,
      });
    }
  };

  stepBackward = (seconds: number) => {
    if (this.isPlaying) {
      this.pause();
    }
    this.frame -= this.fps;
    this.time = (this.time || 0) - 1000 * seconds;

    if (this.callback) {
      this.callback({
        time: this.time,
        frame: this.frame,
      });
    }
  };

  pause = () => {
    this.isPlaying = false;
    this.time = null;
    this.frame = -1;

    // if (this.isPlaying) {
    if (this.tref) {
      cancelAnimationFrame(this.tref);
    }
    // }
  };
}

export class FpsRecordCtrl {
  isPlaying = false;

  time: number = 0;

  timeIncrement: number = 66;

  timeEnd: number = 1000;

  timeStart: number = 0;

  frame = -1;

  callback: undefined | FpsRecordCallback = undefined;

  constructor(
    timeIncrement: number,
    timeStart: number,
    timeEnd: number,
    callback: FpsRecordCallback,
  ) {
    this.time = timeStart;
    this.timeStart = timeStart;
    this.timeIncrement = timeIncrement;
    this.timeEnd = timeEnd;
    this.callback = callback;
  }

  requestFrame = async (cb: Function) => {
    if (cb
      && this.isPlaying
      && (this.timeIncrement * this.frame + this.timeStart) < this.timeEnd
    ) {
      await cb();
    }
  };

  cancelAnimation() {
    this.isPlaying = false;
    this.time = 0;
  }

  loop = async () => {
    this.frame += 1;
    this.time = this.timeIncrement * this.frame + this.timeStart;

    if (this.callback) {
      await this.callback({
        time: this.time,
        frame: this.frame,
      });
    }

    await this.requestFrame(this.loop);
  };

  start = async () => {
    if (!this.isPlaying) {
      this.isPlaying = true;
      await this.requestFrame(this.loop);
    }
  };

  pause = () => {
    this.cancelAnimation();
  };
}

/**
 * Checks if the current time is near the end of any interval within a specified threshold.
 *
 * @param {number} curTime - The current time in milliseconds.
 * @param {PlayerDataIntervals} dataIntervals - An array of time intervals represented as pairs of start and end times.
 * @param {number} [threshold=20000] - The threshold in milliseconds to consider as "near the end" of an interval. Default is 20,000 milliseconds.
 * @param {Function} [cb] - Optional callback function to be invoked if the current time is near the end of an interval.
 *
 * @returns {void}
 */
export const checkNearEndOfInterval = (
  curTime: number,
  dataIntervals: PlayerDataIntervals,
  cb?: Function,
  threshold: number = 60000,
): void => {
  const isNearEnd = dataIntervals.find((interval) => {
    if (!interval[1]) return false;
    const insideInterval = interval[0] <= curTime && curTime <= interval[1];
    return insideInterval && Math.abs(interval[1] - curTime) <= threshold;
  });

  if (isNearEnd) {
    if (cb) {
      cb(isNearEnd);
    }
  }
};

export const enterFullscreen = async (elem: null | HTMLDivElement) => {
  if (elem) {
    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    // @ts-ignore
    } else if (elem.mozRequestFullScreen) { // Firefox
      // @ts-ignore
      elem.mozRequestFullScreen();
      // @ts-ignore
    } else if (elem.webkitRequestFullscreen) { // Chrome, Safari, and Opera
      // @ts-ignore
      elem.webkitRequestFullscreen();
    }
  }
};

export const exitFullscreen = async () => {
  if (document.exitFullscreen) {
    await document.exitFullscreen();
    // @ts-ignore
  } else if (document.mozCancelFullScreen) {
    // @ts-ignore
    document.mozCancelFullScreen();
    // @ts-ignore
  } else if (document.webkitExitFullscreen) {
    // @ts-ignore
    document.webkitExitFullscreen();
  }
};
